import { gql } from "@apollo/client";

import { messageToolLabel } from "../../helpers/recentMessages";

/**
 * GraphQL operations for Captured Chats. These map 1:1 to the admin-only
 * resolver in `api/src/messageCapture/messageCapture.resolver.ts`.
 */

export interface CapturedConversation {
  memberId: string;
  memberName?: string | null;
  memberEmail?: string | null;
  lastMessageAt: string;
  lastText: string;
  lastFromOperator: boolean;
  messageCount: number;
  replyCount: number;
  campaigns: string[];
}

export interface CapturedMessageItem {
  messageId: string;
  fromOperator: boolean;
  text: string;
  sentAt: string;
  campaign?: string | null;
}

export interface CapturedCampaign {
  campaign: string;
  messageCount: number;
  replyCount: number;
}

export interface CaptureStatus {
  running: boolean;
  lastScanAt?: string | null;
  threads: number;
  messages: number;
  replies: number;
  newestMessageAt?: string | null;
}

export const CAPTURED_CONVERSATIONS = gql`
  query AdminCapturedConversations($search: String, $campaign: String, $days: Int) {
    adminCapturedConversations(search: $search, campaign: $campaign, days: $days) {
      memberId
      memberName
      memberEmail
      lastMessageAt
      lastText
      lastFromOperator
      messageCount
      replyCount
      campaigns
    }
  }
`;

export const CAPTURED_THREAD = gql`
  query AdminCapturedThread($memberId: String!, $days: Int) {
    adminCapturedThread(memberId: $memberId, days: $days) {
      messageId
      fromOperator
      text
      sentAt
      campaign
    }
  }
`;

export const CAPTURED_CAMPAIGNS = gql`
  query AdminCapturedCampaigns($days: Int) {
    adminCapturedCampaigns(days: $days) {
      campaign
      messageCount
      replyCount
    }
  }
`;

export const CAPTURE_STATUS = gql`
  query AdminMessageCaptureStatus {
    adminMessageCaptureStatus {
      running
      lastScanAt
      threads
      messages
      replies
      newestMessageAt
    }
  }
`;

export const RUN_CAPTURE = gql`
  mutation AdminRunMessageCapture {
    adminRunMessageCapture {
      running
      lastScanAt
      threads
      messages
      replies
      newestMessageAt
    }
  }
`;

/**
 * A readable name for a campaign tag.
 *
 * Falls back to the raw tag rather than hiding it: `messageToolLabel` only knows
 * the four tools it was written for, and a tag it doesn't recognise is still
 * more useful shown than swallowed.
 */
export const campaignLabel = (campaign: string): string => messageToolLabel(campaign) ?? campaign;

/** "3m ago" / "2h ago" / "5d ago" — enough precision for a chat list. */
export const relativeTime = (iso: string): string => {
  const minutes = Math.floor((Date.now() - new Date(iso).getTime()) / 60000);
  if (minutes < 1) {
    return "just now";
  }
  if (minutes < 60) {
    return `${minutes}m ago`;
  }
  const hours = Math.floor(minutes / 60);
  if (hours < 24) {
    return `${hours}h ago`;
  }
  return `${Math.floor(hours / 24)}d ago`;
};
